/**
 * The composer slot: type without spending a pane first (RFC 0284 S8d).
 *
 * THE ASK. You are reading a document, the thread you were in is not on screen,
 * and you want to say something. Today that is ⌘T, which opens a pane, which
 * pushes the document you were reading aside to make room for a box you are
 * about to type one sentence into. The pane is the cost, and the sentence is
 * the point.
 *
 * WHY IT IS A SLOT. A layout has to be able to place it, and a layout names
 * slots. It is chrome by RFC 0266's rule as well: never two, never split,
 * outside the content tree. The three that existed were all wrong for it. The
 * sidebar and the panel are columns; the header is at the top and already holds
 * the space tabs, so putting a composer there would make a person choose
 * between the two. So `Slot` gained a fourth member and the shell lays this one
 * out along the bottom of the content area.
 *
 * IT CONTINUES, IT DOES NOT ONLY START. The first version was hardwired to
 * start a new session, which read the ask too literally: "no pane" became "no
 * session either", and every sentence typed into the strip opened a new thread
 * nobody was in. What a person means by the box at the bottom of the window is
 * almost always the conversation they were just having. So the default target
 * is the space's most recent session and starting a new one is a choice in the
 * menu, which is the inversion this file is mostly about.
 *
 * WHERE "MOST RECENT IN THIS SPACE" COMES FROM. The working set (S4c,
 * `recentsCapability`), which is what has been reached for in this space, most
 * recent first. Not the window's panes: a session can be the one you were last
 * in with no pane holding it, which is exactly the case the strip is for. The
 * shell resolves the ids and hands over the sessions; the fill never
 * learns where the order came from.
 *
 * WHY IT DRAWS NOTHING WHEN AN AGENT PANE IS OPEN. Every one of them carries a
 * composer already, and two boxes one above the other is a choice to make before
 * typing. The strip is for a space where there is no box at all.
 *
 * A version of this tried the narrower rule, hiding only when the TARGET's own
 * thread was on screen, on the theory that two boxes are confusing only when
 * they send to the same place. The session list broke it: an agent pane holding
 * no session id, so it never matched the target, and a person on the session
 * list got a second composer under the one the list already draws.
 *
 * THE CONDITION IS THE CONTRIBUTION'S, not the slot's. The shell hands over what
 * is on screen and lets the fill decide. A replacement can draw always,
 * or only when something is running, or never on a Sunday, without the shell
 * learning a setting for each.
 *
 * IT SENDS THROUGH THE SHELL, passed in as `onAsk`. There is no second
 * implementation of starting or of sending here, and `thread/startSession.ts`
 * is now the only one of either: a first message that skips the title, or goes
 * to a different model than the composer would have used, or lands in a project
 * nothing hosts, is the kind of inconsistency nobody thinks to report as a bug.
 */

import { Check, ChevronDown, Maximize2 } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Composer, type ComposerProps } from '../../../features/thread/Composer'
import { ComposerSlotProvider } from '../../../features/thread/ComposerSlot'
import { SessionMark } from '../../../services/sessions/SessionMark'
import type { BuiltinFill } from '../slots'

/** A session the strip could be talking to. Thin on purpose: the shell resolves
 *  the working set against the live catalog, and a row that carried more would
 *  be a third session shape beside `SessionRow` and `NewPaneItem`. */
export interface ComposerTarget {
  id: string
  title: string
  /** "waiting on you", "working" — the same words the new-pane page uses, and
   *  the shell's to choose, since only it can read a status. */
  note?: string
}

export interface ComposerSlotProps {
  /**
   * Where the message goes. Null is a NEW session, which is a choice now rather
   * than the only behaviour.
   *
   * Resolved by the shell rather than looked up here from an id, so the box and
   * the send agree by construction: a target that has fallen out of the working
   * set cannot be named on screen while `onAsk` still routes to it.
   */
  target: ComposerTarget | null
  /** What the menu offers, most recent first. Empty means the only choice is a
   *  new session, and the menu says so rather than being drawn empty. */
  sessions: ComposerTarget[]
  /** Point the strip somewhere else. Null asks for a new session. */
  onSelectTarget: (sessionId: string | null) => void
  /**
   * Sessions whose thread is open in THIS space. The space, not the window:
   * spaces are how a person separates bodies of work (RFC 0281), and a thread
   * open in another one is not on screen and cannot take what you type.
   *
   * The shipped composer does not read this, and it stays because a replacement
   * is the thing most likely to want it: "draw beside a thread as long as I am
   * not pointed at it" is the judgement this one tried and dropped.
   */
  openSessionIds: string[]
  /**
   * Whether this space holds any agent pane at all, which is what the shipped
   * composer decides on.
   *
   * Wider than `openSessionIds` and that is the point: a session LIST pane holds
   * no session id, so it appears in neither that list nor any thread, and it
   * carries a composer all the same.
   */
  hasThread: boolean
  /**
   * Send this message to the current target: to the session when there is one,
   * as a new session when there is not.
   *
   * The full composer signature rather than a bare string, because the model,
   * the skills named with `/` and the files attached are choices about THIS
   * message and are lost if only the text survives.
   */
  onAsk: ComposerProps['onSend']
  /** Attachments, skills and projects: the rest of the real composer, shared
   *  with the new-pane page so the two boxes cannot drift. */
  composer: Partial<ComposerProps>
  /** Only the focused window takes the caret. Two composers racing for it on
   *  every render is what an unconditional `autoFocus` buys. */
  autoFocus?: boolean
  /** What the target session has in hand (RFC 0297), on the line above the
   *  box. The shell draws it, because only the shell knows the target's
   *  project, and it is given the session menu and the way into the thread to
   *  draw on that same line. Absent for a new session, which holds nothing. */
  holdings?: (line: { leading: ReactNode; trailing: ReactNode }) => ReactNode
  /** What the target session's agent is doing while it runs, one line with a
   *  way into the thread. The shell draws it; it draws nothing when idle. */
  activity?: ReactNode
  /** Open the target session's thread in a pane. Drawn at the right end of
   *  the holdings line, so the way into the thread sits with the choice of
   *  which thread, not inside the reply it would otherwise crowd. */
  onOpenTarget?: () => void
}

export const BUILTIN_COMPOSER_ID = 'mod.composer'

/** Enough to choose from without becoming a session list. Past this the menu is
 *  the thing ⌘K is for. */
const TARGET_CAP = 8

/**
 * The space's sessions, most recently reached for first.
 *
 * PURE, and out here rather than inline in the shell, because the first element
 * of what it returns is the strip's default target: "continue the conversation
 * you were just in" is the whole inversion S8d needed, and a claim that
 * load-bearing should be checkable without a rendered window.
 *
 * `recentIds` holds documents and sessions together (`useOpenItems`), in the
 * order they were reached. Resolving against the live session list drops the
 * documents and the deleted in one pass: an id in neither list opens nothing,
 * so offering to message it would be a row that sends into a hole.
 */
export function sessionsInReach(
  recentIds: readonly string[],
  sessions: readonly { id: string; title: string }[],
  note: (id: string) => string | undefined,
): ComposerTarget[] {
  const byId = new Map(sessions.map((s) => [s.id, s]))
  const out: ComposerTarget[] = []
  const taken = new Set<string>()
  for (const id of recentIds) {
    const row = byId.get(id)
    if (!row || taken.has(id)) continue
    taken.add(id)
    out.push({ id, title: row.title, note: note(id) })
    if (out.length >= TARGET_CAP) break
  }
  return out
}

/**
 * Whether the strip has anything to add to what is already on screen.
 *
 * Exported so the rule is testable as itself rather than through a rendered
 * tree: it is the whole of this fill's judgement, and the thing a
 * replacement would most want to disagree with.
 */
export function composerDraws({
  hasThread,
}: Pick<ComposerSlotProps, 'hasThread'>): boolean {
  // ANY AGENT PANE ALREADY HAS A COMPOSER IN IT, and that is the whole rule.
  //
  // An earlier version asked the narrower question, whether the TARGET's own
  // thread was on screen, on the theory that two boxes are only confusing when
  // they send to the same place. The session list is the case that breaks it: it
  // is an agent pane holding no session id, so it never appeared in
  // `openSessionIds`, and a person sitting on the session list with no thread
  // open got a second composer under the one the list already draws.
  //
  // The narrower rule could be patched to special-case that, and it should not
  // be. What a person sees is a box they can type into, and a second one below
  // it is a choice to make before typing whatever the two are addressed to. The
  // strip is for when there is NO box on screen.
  return !hasThread
}

export const builtinComposer: BuiltinFill<'composer'> = {
  id: BUILTIN_COMPOSER_ID,
  title: 'Composer',
  slot: 'composer',
  builtin: true,
  render: ({
    target,
    sessions,
    onSelectTarget,
    hasThread,
    onAsk,
    composer,
    autoFocus = false,
    holdings,
    activity,
    onOpenTarget,
  }) => {
    // NULL, not an empty box. The shell draws the strip over the panes, so an
    // empty one would be a band of background floating across the bottom of
    // whatever you are reading.
    if (!composerDraws({ hasThread })) return null
    const menu = (
      <SessionMenu
        target={target}
        sessions={sessions}
        onSelect={onSelectTarget}
      />
    )
    const openThread =
      target && onOpenTarget ? (
        <button
          type="button"
          onClick={onOpenTarget}
          aria-label="Open this session's thread"
          title="Open this session's thread"
          className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <Maximize2 className="size-3" strokeWidth={2.25} />
        </button>
      ) : null
    // The line above the box: the menu, then what the session holds, then the
    // way into its thread. A new session holds nothing, so its line is the
    // menu and, beside it, the project and branch it will work in: one line,
    // not the menu over a second row that answers the same "where" question.
    // The composer hands those controls over (`where`), since it owns the
    // choice they write to.
    const line = (where: ReactNode) =>
      target && holdings ? (
        holdings({ leading: menu, trailing: openThread })
      ) : (
        <div className="flex w-full min-w-0 items-center gap-2 px-1 pb-1.5 text-xs">
          {menu}
          {where ? (
            <div className="flex min-w-0 items-center gap-0.5">{where}</div>
          ) : null}
          <span className="min-w-0 flex-1" />
          {openThread}
        </div>
      )
    return (
      // `compact`: the tight floating box, which is what a strip over a
      // document wants. It used to be ruled out because the variant doubled as
      // "portal me somewhere" and this has nowhere to go; the scale and the
      // destination are separate questions now (see ComposerSlot.tsx), and a
      // null element means "draw where you stand". Its own provider, so this
      // composer's variant cannot be changed by whatever wraps the shell.
      <ComposerSlotProvider value={{ variant: 'compact', element: null }}>
        <div className="mx-auto flex w-full max-w-[600px] flex-col items-start gap-1 px-3 pb-1">
          <div className="w-full">
            <Composer
              {...composer}
              onSend={onAsk}
              placeholder={target ? `Message ${target.title}` : 'Message'}
              autoFocus={autoFocus}
              // A session that has already spoken cannot be moved between
              // projects (RFC 0290 S6), so the project switcher goes quiet the
              // moment the strip is pointed at one. Only a new session leaves
              // the choice open, the same as on the new-pane page.
              unstarted={!target}
              {...(target ? { sessionId: target.id } : {})}
              holdings={line}
              // Inside the box, under the holdings bar, so the reply (RFC 0304
              // 3g) shares the frame and the width of what you type into.
              {...(target && activity ? { activity } : {})}
            />
          </div>
        </div>
      </ComposerSlotProvider>
    )
  },
}

/**
 * Which session the strip is talking to.
 *
 * A CAPSULE ABOVE THE BOX rather than a control inside it. The composer's own
 * control row belongs to choices about the MESSAGE — the model, the skills, the
 * files — and the target is a choice about the conversation. Putting it in that
 * row would also put a session picker in every thread, which is a control that
 * means nothing where the pane already answers it.
 *
 * The menu is the same shape as the composer's project and model menus, opening
 * upward and closing on an outside click, because a strip at the bottom of the
 * window has no room below it.
 */
function SessionMenu({
  target,
  sessions,
  onSelect,
}: {
  target: ComposerTarget | null
  sessions: ComposerTarget[]
  onSelect: (sessionId: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Which session this goes to"
        className="flex max-w-[180px] items-center gap-1.5 rounded font-medium text-foreground/80 text-xs transition-colors hover:text-foreground"
      >
        {/* The same mark as the thread pane's header (RFC 0311). */}
        {target ? <SessionMark id={target.id} size={14} /> : null}
        <span className="truncate">
          {target ? target.title : 'New session'}
        </span>
        <ChevronDown className="size-3 shrink-0" strokeWidth={2.5} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-10 mb-1.5 max-h-72 min-w-64 overflow-y-auto rounded-xl border border-border bg-background py-1 shadow-lg"
        >
          <MenuRow
            label="New session"
            selected={target === null}
            onClick={() => {
              setOpen(false)
              onSelect(null)
            }}
          />
          {sessions.length > 0 ? (
            <div className="my-1 border-border/60 border-t" />
          ) : null}
          {sessions.map((s) => (
            <MenuRow
              key={s.id}
              markId={s.id}
              label={s.title}
              note={s.note}
              selected={s.id === target?.id}
              onClick={() => {
                setOpen(false)
                onSelect(s.id)
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function MenuRow({
  markId,
  label,
  note,
  selected,
  onClick,
}: {
  /** A session row draws its mark; "New session" has none. */
  markId?: string
  label: string
  note?: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left hover:bg-muted"
    >
      {markId ? <SessionMark id={markId} size={14} /> : null}
      <span className="flex-1 truncate text-sm">{label}</span>
      {note ? (
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {note}
        </span>
      ) : null}
      {selected ? (
        <Check className="size-3 shrink-0" strokeWidth={2.5} />
      ) : null}
    </button>
  )
}
